/**
 * Patch 35.3a · Lifecycle-driven action controls.
 *
 * Reads the canonical lifecycle from the backend so the drawer never
 * duplicates state logic. When an asset is approved, Approve
 * disappears immediately. The primary action button reflects the
 * actual next stage (Render Image / Choose Hero / Render Video /
 * Approve Final / Schedule / Leave in Vault).
 *
 * Patch 35.3a.1 · truthful Vault reporting.
 * Patch 35.3c · Schedule button is now fully functional — opens the
 * ScheduleDrawer for eligible (approved + Vault-registered) final
 * deliverables. Unschedule cancels the earliest queued schedule for
 * this asset and lets the backend flip the asset back to approved.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CalendarClock, Send, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import type { GeneratedAsset } from "@/entities/ai-studio";
import { fetchAssetLifecycle, getAssetLabel } from "@/entities/ai-studio";
import { ScheduleDrawer } from "@/entities/ai-studio/ScheduleDrawer";
import { cancelSchedule, getCalendar } from "@/entities/mission-control";
type AssetStatus = "draft" | "needs_review" | "approved" | "scheduled" | "published" | "archived";

interface Props {
  workspaceId: string;
  workspaceTimezone?: string;
  asset: GeneratedAsset;
  onTransition: (to: AssetStatus) => Promise<void>;
}

const PRIMARY_LABEL: Record<string, string> = {
  approve: "Approve",
  approve_final: "Approve Final",
  schedule: "Schedule",
  render_image: "Render Image",
  render_video: "Render Video",
  select_hero: "Choose Hero Image",
};

const SECONDARY_LABEL: Record<string, string> = {
  return: "Return",
  edit: "Edit",
  archive: "Archive",
  regenerate: "Regenerate",
  view_source: "View Source",
  change_hero: "Change Image",
  leave_in_vault: "Leave in Vault",
  edit_as_new_version: "Edit as New Version",
  reuse_as_new_version: "Reuse as New Version",
  edit_schedule: "Edit Schedule",
  unschedule: "Unschedule",
  view: "View",
};

export function LifecycleActions({
  workspaceId, workspaceTimezone, asset, onTransition,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const q = useQuery({
    queryKey: ["asset-lifecycle", workspaceId, asset.id, asset.status, asset.is_head, asset.superseded],
    queryFn: () => fetchAssetLifecycle(workspaceId, asset.id),
    enabled: !!workspaceId && !!asset.id,
    staleTime: 10_000,
  });

  // Patch 35.3c · fetch the active schedules for this asset so
  // Unschedule and Edit Schedule act on real rows.
  const calendarQ = useQuery({
    queryKey: ["calendar", workspaceId],
    queryFn: () => getCalendar(workspaceId),
    enabled: !!workspaceId && ((asset.status as string) === "scheduled" || (asset.status as string) === "published"),
    staleTime: 30_000,
  });
  const assetSchedules = (calendarQ.data || [])
    .filter((e) => e.asset_id === asset.id
      && e.publishing_status !== "cancelled"
      && e.publishing_status !== "published");
  const nextSchedule = assetSchedules[0] ?? null;

  const unschedule = useMutation({
    mutationFn: async () => {
      if (!nextSchedule) throw new Error("No active schedule to cancel.");
      return cancelSchedule(workspaceId, nextSchedule.id);
    },
    onSuccess: () => {
      toast.success("Unscheduled");
      void qc.invalidateQueries({ queryKey: ["asset-lifecycle", workspaceId, asset.id] });
      void qc.invalidateQueries({ queryKey: ["pack-summary", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["ops-pack-assets", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["calendar", workspaceId] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Couldn't unschedule right now.");
    },
  });

  const lc = q.data;
  const primary = lc?.primary_action;
  const stage = lc?.lifecycle_stage;
  const vaultStatus = lc?.vault_status;

  const doApprove = async () => {
    await onTransition("approved");
    void qc.invalidateQueries({ queryKey: ["asset-lifecycle", workspaceId, asset.id] });
    void qc.invalidateQueries({ queryKey: ["pack-summary", workspaceId] });
  };

  const isApprovedFinal = stage === "approved_rendered" || stage === "approved_text";
  // Patch 35.3c · Schedule button is enabled only when the asset is a
  // final deliverable AND Vault registration is complete. Approved
  // (but not-yet-registered) assets get a helpful nudge instead of
  // a disabled button.
  const canSchedule = isApprovedFinal && vaultStatus === "registered";

  return (
    <div className="mt-3 space-y-3" data-testid="lifecycle-actions">
      {/* Approved final — truthful Vault banner */}
      {isApprovedFinal && vaultStatus === "registered" && (
        <div
          data-testid={`lifecycle-confirmation-${stage}`}
          className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
          <div>
            <div className="font-medium text-emerald-700 dark:text-emerald-400">
              Approved and stored in Vault
            </div>
            <div className="text-muted-foreground">{lc?.next_step_message}</div>
          </div>
        </div>
      )}

      {isApprovedFinal && vaultStatus === "eligible_not_registered" && (
        <div
          data-testid={`lifecycle-confirmation-${stage}-pending`}
          className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs"
        >
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div>
            <div className="font-medium text-amber-700 dark:text-amber-400">
              Approved · Vault registration pending
            </div>
            <div className="text-muted-foreground">
              This asset is eligible for the Vault but has not yet been
              registered. Retry the approval action to complete
              registration.
            </div>
          </div>
        </div>
      )}

      {stage === "approved_image_prompt" && (
        <div
          data-testid="lifecycle-confirmation-approved_image_prompt"
          className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <div className="font-medium">Prompt approved</div>
            <div className="text-muted-foreground">{lc?.next_step_message}</div>
          </div>
        </div>
      )}

      {stage === "scheduled" && (
        <div
          data-testid="lifecycle-confirmation-scheduled"
          className="flex items-start gap-2 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-xs"
        >
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
          <div>
            <div className="font-medium text-sky-700 dark:text-sky-400">
              Scheduled
            </div>
            <div className="text-muted-foreground">
              {nextSchedule
                ? `${nextSchedule.channel_label} · ${nextSchedule.local_preview_at}`
                : lc?.next_step_message}
              {assetSchedules.length > 1 && ` · +${assetSchedules.length - 1} more`}
            </div>
          </div>
        </div>
      )}

      {stage === "published" && (
        <div
          data-testid="lifecycle-confirmation-published"
          className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs"
        >
          <Send className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
          <div>
            <div className="font-medium text-emerald-700 dark:text-emerald-400">
              Published
            </div>
            <div className="text-muted-foreground">{lc?.next_step_message}</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {primary === "approve" && (
          <Button size="sm" data-testid="ops-asset-drawer-approve" onClick={doApprove}>
            {PRIMARY_LABEL[primary]}
          </Button>
        )}
        {primary === "approve_final" && (
          <Button size="sm" data-testid="ops-asset-drawer-approve-final" onClick={doApprove}>
            {PRIMARY_LABEL[primary]}
          </Button>
        )}
        {primary === "schedule" && (
          <Button
            size="sm"
            data-testid="ops-asset-drawer-schedule"
            onClick={() => setScheduleOpen(true)}
            disabled={!canSchedule}
          >
            {PRIMARY_LABEL[primary]}
          </Button>
        )}
        {primary === "select_hero" && (
          <div className="text-xs text-muted-foreground">
            {lc?.next_step_message}
          </div>
        )}
        {primary === "render_image" && (
          <div
            data-testid="lifecycle-primary-render_image"
            className="text-xs text-muted-foreground"
          >
            Use <span className="font-medium">Regenerate</span> to render this prompt into an image.
          </div>
        )}
        {primary === "render_video" && (
          <div className="text-xs text-muted-foreground">
            Use the video render button above to render this kit.
          </div>
        )}

        {/* Schedule editing / removal — only when scheduled. */}
        {(lc?.available_actions || []).includes("edit_schedule") && (
          <Button
            size="sm"
            variant="outline"
            data-testid="ops-asset-drawer-edit-schedule"
            onClick={() => setScheduleOpen(true)}
            disabled={!nextSchedule || unschedule.isPending}
          >
            {SECONDARY_LABEL.edit_schedule}
          </Button>
        )}
        {(lc?.available_actions || []).includes("unschedule") && (
          <Button
            size="sm"
            variant="outline"
            data-testid="ops-asset-drawer-unschedule"
            onClick={() => unschedule.mutate()}
            disabled={!nextSchedule || unschedule.isPending}
          >
            {unschedule.isPending ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Unscheduling…
              </>
            ) : (
              SECONDARY_LABEL.unschedule
            )}
          </Button>
        )}
        {(lc?.available_actions || []).includes("reuse_as_new_version") && (
          <Button size="sm" variant="outline" data-testid="ops-asset-drawer-reuse" disabled>
            {SECONDARY_LABEL.reuse_as_new_version}
          </Button>
        )}
        {(lc?.available_actions || []).includes("return") && (
          <Button size="sm" variant="outline" data-testid="ops-asset-drawer-return" onClick={() => void onTransition("needs_review")}>
            {SECONDARY_LABEL.return}
          </Button>
        )}
        {(lc?.available_actions || []).includes("archive") && (
          <Button size="sm" variant="ghost" data-testid="ops-asset-drawer-archive" onClick={() => void onTransition("archived")}>
            {SECONDARY_LABEL.archive}
          </Button>
        )}
      </div>

      {scheduleOpen && (
        <ScheduleDrawer
          workspaceId={workspaceId}
          workspaceTimezone={workspaceTimezone || "UTC"}
          assetId={asset.id}
          assetPreview={`${getAssetLabel(asset)} · ${asset.permanent_asset_id}`}
          onOpenChange={setScheduleOpen}
        />
      )}
    </div>
  );
}
